import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowDown, ArrowRight, ArrowUp, Camera, Check, Lock, MapPin } from "lucide-react";
import { useLang } from "../App";
import { membersI18n, propertiesI18n } from "../i18n";
import { commercialPricingMode, formatAvailability, formatPropertyPrice, formatSoldDate, isCommercialRental } from "./Properties";
import MemberShell from "../components/MemberShell";
import Reveal from "../components/Reveal";
import { getMemberToken, getMemberInfo, clearMemberSession } from "../memberAuth";
import { SoldRibbon } from "../components/SoldRibbon";
import { resolveImg } from "../images";

const API = process.env.REACT_APP_BACKEND_URL;

function DarkCard({ prop, p, i, inquired, badge, newLabel, lang }) {
  const isRental = prop.listing_type === "rental";
  const isRented = isRental && prop.availability_status === "rented";
  return (
    <Reveal delay={(i % 3) * 0.08}>
      <article data-testid={`member-property-card-${prop.id}`} className="group">
        <Link to={`/espace/objets/${prop.id}`} className="block">
          <div className="relative aspect-[4/3] overflow-hidden bg-ivory/5">
            {prop.images && prop.images[0] && (
              <img src={resolveImg(prop.images[0])} alt={prop.title} loading="lazy" draggable={false}
                className="h-full w-full select-none object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]" />
            )}
            {prop.sold ? (
              <SoldRibbon label={p.sold} testid={`member-sold-${prop.id}`} />
            ) : isRented ? (
              <SoldRibbon label={p.rented} testid={`member-rented-${prop.id}`} />
            ) : (
              <span className="absolute left-0 top-0 bg-champagne px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-ink">
                {isRental ? `${p.rental} · Off-market` : "Off-market"}
              </span>
            )}
            {prop.is_new && !prop.sold && !isRented && (
              <span data-testid={`member-new-${prop.id}`}
                className="absolute right-0 top-0 flex items-center gap-1.5 bg-ivory px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-ink">
                <span className="h-1.5 w-1.5 rounded-full bg-bronze" /> {newLabel}
              </span>
            )}
          </div>
        </Link>
        <div className="mt-5 flex items-baseline justify-between gap-4">
          <span className="text-sm font-semibold tracking-wide text-ivory">{formatPropertyPrice(prop, p)}</span>
          <Link to={`/espace/objets/${prop.id}`} data-testid={`member-property-link-${prop.id}`}
            className="group/link flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.2em] text-champagne">
            {p.details}
            <ArrowRight size={13} className="transition-transform duration-500 group-hover/link:translate-x-1" />
          </Link>
        </div>
        <Link to={`/espace/objets/${prop.id}`}>
          <h2 className="mt-3 font-serif text-xl font-medium leading-snug text-ivory transition-colors duration-300 group-hover:text-champagne">
            {lang !== "fr" && prop[`title_${lang}`] ? prop[`title_${lang}`] : prop.title}
          </h2>
        </Link>
        <p className="mt-2 text-[11px] font-medium uppercase tracking-[0.22em] text-ivory/45">{p.categories[prop.category]}</p>
        <p className="mt-1.5 flex items-center gap-1.5 text-xs font-light text-ivory/40">
          <MapPin size={12} /> {prop.zip_code} {prop.city}
        </p>
        {isCommercialRental(prop) && commercialPricingMode(prop) === "per_sqm_year" && Number(prop.rental_price_per_sqm_year) > 0 && (
          <p data-testid={`member-rental-rate-${prop.id}`} className="mt-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-ivory/50">
            CHF {Number(prop.rental_price_per_sqm_year).toLocaleString("de-CH")}.– {p.perSqmYear}
          </p>
        )}
        {isRental && (
          <p data-testid={`member-availability-${prop.id}`} className="mt-2.5 border-l-2 border-champagne/60 pl-3 text-[11px] font-medium uppercase tracking-[0.18em] text-champagne">
            {formatAvailability(prop, p, lang)}
          </p>
        )}
        {prop.ref && (
          <p data-testid={`member-property-ref-${prop.id}`} className="mt-1 text-[10px] uppercase tracking-[0.2em] text-ivory/30">{prop.ref}</p>
        )}
        {prop.sold && prop.sold_at && (
          <p data-testid={`member-sold-date-${prop.id}`} className="mt-2.5 border-l-2 border-champagne/60 pl-3 text-[11px] font-medium uppercase tracking-[0.18em] text-champagne">
            {p.soldOn} {formatSoldDate(prop.sold_at, lang)}
          </p>
        )}
        {inquired && (
          <p data-testid={`member-inquired-${prop.id}`} className="mt-3 inline-flex items-center gap-2 border border-champagne/40 px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-champagne">
            <Check size={11} /> {badge}
          </p>
        )}
      </article>
    </Reveal>
  );
}

export default function MemberArea() {
  const { lang, t } = useLang();
  const m = membersI18n[lang];
  const p = propertiesI18n[lang];
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [stats, setStats] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [convos, setConvos] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const info = getMemberInfo();

  const loadAvatar = () => {
    fetch(`${API}/api/members/avatar`, { headers: { Authorization: `Bearer ${getMemberToken()}` } })
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => {
        if (blob && blob.size > 0) setAvatarUrl((old) => {
          if (old) URL.revokeObjectURL(old);
          return URL.createObjectURL(blob);
        });
      })
      .catch(() => {});
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!getMemberToken()) {
      navigate("/espace");
      return;
    }
    const headers = { Authorization: `Bearer ${getMemberToken()}` };
    Promise.all([
      fetch(`${API}/api/member/properties`, { headers }),
      fetch(`${API}/api/member/stats`, { headers }),
      fetch(`${API}/api/member/conversations`, { headers }),
    ])
      .then(async ([r1, r2, r3]) => {
        if (!r1.ok) throw new Error("auth");
        setItems((await r1.json()).properties);
        if (r2.ok) setStats(await r2.json());
        if (r3.ok) setConvos((await r3.json()).conversations);
      })
      .catch(() => {
        clearMemberSession();
        navigate("/espace");
      });
    loadAvatar();
  }, [navigate]);

  const uploadAvatar = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/api/members/avatar`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getMemberToken()}` },
        body: fd,
      });
      if (!res.ok) throw new Error("failed");
      loadAvatar();
    } catch {
      /* silent — upload stays optional */
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const interests = info?.interests || [];
  const matches = (items || []).filter((x) => interests.length === 0 || interests.includes(x.category));
  const remaining = (items || []).filter((x) => !matches.some((match) => match.id === x.id));
  const inquiredTitles = stats?.inquired_titles || [];
  const wasInquired = (prop) => inquiredTitles.includes(prop.title);

  const statBlocks = stats
    ? [
        { label: m.statInquiries, value: stats.inquiries_total, testid: "stat-inquiries" },
        { label: m.statOpen, value: stats.inquiries_open, testid: "stat-open" },
        { label: m.statAnswered, value: stats.inquiries_answered, testid: "stat-answered" },
        { label: m.statViews, value: stats.objects_viewed, testid: "stat-views" },
      ]
    : [];

  return (
    <MemberShell>
      <section data-testid="member-area" className="px-6 pb-28 pt-14 md:px-12 lg:px-20">
        {/* Welcome + coordinates */}
        <Reveal>
          <p className="flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.3em] text-champagne">
            <Lock size={12} /> {m.navLabel}
          </p>
          <h1 className="mt-6 font-serif text-4xl font-medium leading-tight text-ivory sm:text-5xl lg:text-6xl">
            {m.welcome} {info ? info.first_name : ""}
          </h1>
          <p className="mt-4 max-w-xl text-sm font-light leading-relaxed text-ivory/55">{m.dashboardSub}</p>
          {stats && stats.new_since > 0 && (
            <p data-testid="member-new-since" className="mt-4 inline-block border-l-2 border-bronze pl-4 text-xs font-light italic text-champagne">
              {stats.new_since === 1 ? m.newSinceOne : m.newSinceMany.replace("{n}", stats.new_since)}
            </p>
          )}
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-12 border border-ivory/12 p-8 md:p-10" data-testid="member-details">
            <div className="flex flex-wrap items-center gap-8">
              <div className="relative">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" data-testid="member-avatar"
                    className="h-20 w-20 rounded-full border border-bronze/40 object-cover" />
                ) : (
                  <div data-testid="member-avatar-fallback"
                    className="flex h-20 w-20 items-center justify-center rounded-full border border-ivory/20 font-serif text-2xl text-champagne">
                    {info ? `${info.first_name[0]}${info.last_name[0]}` : "–"}
                  </div>
                )}
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                  data-testid="avatar-file-input" onChange={(e) => uploadAvatar(e.target.files[0])} />
              </div>
              <div>
                <h2 className="text-[11px] font-medium uppercase tracking-[0.3em] text-champagne">{m.yourDetails}</h2>
                <button
                  data-testid="avatar-upload-button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="mt-3 flex items-center gap-2 border border-ivory/20 px-4 py-2 text-[10px] uppercase tracking-[0.18em] text-ivory/60 transition-colors duration-300 hover:border-champagne hover:text-champagne disabled:opacity-50"
                >
                  <Camera size={12} /> {uploading ? "…" : avatarUrl ? m.avatarChange : m.avatarAdd}
                </button>
              </div>
            </div>
            <div className="mt-8 grid gap-x-10 gap-y-6 text-sm sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-ivory/40">{t.contact.lastName}</p>
                <p className="mt-1.5 font-light text-ivory" data-testid="member-name">
                  {info ? `${info.first_name} ${info.last_name}` : "—"}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-ivory/40">{m.company}</p>
                <p className="mt-1.5 font-light text-ivory" data-testid="member-company">{info?.company || "—"}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-ivory/40">E-mail</p>
                <p className="mt-1.5 font-light text-ivory" data-testid="member-email">{info?.email || "—"}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-ivory/40">{m.memberSince}</p>
                <p className="mt-1.5 font-light text-ivory">
                  {info?.created_at
                    ? new Date(info.created_at).toLocaleDateString({ fr: "fr-CH", de: "de-CH", en: "en-GB" }[lang] || "fr-CH", { dateStyle: "long" })
                    : "—"}
                </p>
              </div>
            </div>
            <div className="mt-8 border-t border-ivory/10 pt-6">
              <p className="text-[10px] uppercase tracking-[0.2em] text-ivory/40">{m.yourInterests}</p>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="member-interests">
                {interests.length === 0 ? (
                  <span className="border border-ivory/20 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-ivory/60">
                    {m.allInterests}
                  </span>
                ) : (
                  interests.map((key) => (
                    <span key={key} className="border border-bronze/50 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-champagne">
                      {p.categories[key] || key}
                    </span>
                  ))
                )}
              </div>
            </div>
          </div>
        </Reveal>

        {/* Stats */}
        {statBlocks.length > 0 && (
          <div className="mt-12 grid grid-cols-2 gap-px bg-ivory/10 lg:grid-cols-4" data-testid="member-stats">
            {statBlocks.map((s, i) => (
              <Reveal key={s.testid} delay={0.15 + i * 0.06}>
                <div className="bg-ink px-6 py-8" data-testid={s.testid}>
                  <p className="font-serif text-4xl font-medium text-ivory lg:text-5xl">{s.value}</p>
                  <p className="mt-2 text-[10px] uppercase tracking-[0.22em] text-ivory/45">{s.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        )}

        {/* Messages */}
        {convos && convos.length > 0 && (
          <div className="mt-14" data-testid="member-messages">
            <Reveal>
              <div className="flex items-end justify-between border-b border-ivory/10 pb-6">
                <h2 className="flex items-center gap-4 font-serif text-3xl font-medium text-ivory md:text-4xl">
                  {m.messages}
                  {convos.some((cv) => cv.unread > 0) && (
                    <span
                      data-testid="member-messages-unread-total"
                      className="flex h-7 w-7 items-center justify-center rounded-full bg-champagne font-sans text-xs font-semibold text-ink"
                    >
                      {convos.reduce((n, cv) => n + cv.unread, 0)}
                    </span>
                  )}
                </h2>
                <p className="text-[11px] uppercase tracking-[0.25em] text-ivory/40">{convos.length}</p>
              </div>
            </Reveal>
            <div className="divide-y divide-ivory/10">
              {convos.map((cv) => (
                <button
                  key={cv.id}
                  data-testid={`member-convo-${cv.id}`}
                  onClick={() => navigate(`/espace/objets/${cv.property_id}`, { state: { openChat: true } })}
                  className="group flex w-full items-baseline justify-between gap-6 py-5 text-left"
                >
                  <div>
                    <p className="font-serif text-lg text-ivory transition-colors duration-300 group-hover:text-champagne">
                      {cv.property_title}
                      {cv.property_ref && (
                        <span className="ml-3 text-[10px] uppercase tracking-[0.2em] text-bronze">{cv.property_ref}</span>
                      )}
                    </p>
                    {cv.last_message && (
                      <p className={`mt-1 line-clamp-1 text-xs font-light ${cv.unread > 0 ? "text-ivory/75" : "text-ivory/45"}`}>
                        {cv.last_message.sender === "admin" ? "FLEN" : m.you} · {cv.last_message.body}
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-4">
                    {cv.unread > 0 && (
                      <span
                        data-testid={`member-convo-unread-${cv.id}`}
                        className="flex h-6 w-6 items-center justify-center rounded-full bg-champagne text-[11px] font-semibold text-ink"
                      >
                        {cv.unread}
                      </span>
                    )}
                    <ArrowRight size={14} className="text-champagne transition-transform duration-300 group-hover:translate-x-1" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Matching selection */}
        <div className="mt-24">
          <Reveal>
            <div className="flex items-end justify-between gap-6 border-b border-ivory/10 pb-6">
              <div>
                <h2 className="font-serif text-3xl font-medium text-ivory md:text-4xl">{m.forYou}</h2>
                <p className="mt-2 text-sm font-light text-ivory/50">{m.forYouSub}</p>
              </div>
              <p className="text-[11px] uppercase tracking-[0.25em] text-ivory/40" data-testid="matches-count">
                {matches.length}
              </p>
            </div>
          </Reveal>
          {!items ? (
            <p className="mt-16 text-sm text-ivory/50">…</p>
          ) : matches.length === 0 ? (
            <p data-testid="member-no-matches" className="mt-16 max-w-md font-serif text-2xl italic text-ivory/55">{m.noMatches}</p>
          ) : (
            <div className="mt-10 grid gap-x-10 gap-y-16 md:grid-cols-2 lg:grid-cols-3" data-testid="member-matches-grid">
              {matches.map((prop, i) => (
                <DarkCard key={prop.id} prop={prop} p={p} i={i} inquired={wasInquired(prop)} badge={m.inquiredBadge} newLabel={m.newBadge} lang={lang} />
              ))}
            </div>
          )}
        </div>

        {/* Show all */}
        {items && items.length > 0 && matches.length !== items.length && (
          <div className="mt-24 text-center">
            <button
              data-testid="member-show-all"
              onClick={() => {
                setShowAll(!showAll);
                if (!showAll) setTimeout(() => document.getElementById("all-objects")?.scrollIntoView({ behavior: "smooth" }), 100);
              }}
              className="group inline-flex items-center gap-4 border border-champagne/50 px-10 py-5 text-[11px] font-semibold uppercase tracking-[0.25em] text-champagne transition-colors duration-500 hover:bg-champagne hover:text-ink"
            >
              {showAll ? m.hideAll : m.showAll}
              {showAll ? <ArrowUp size={15} /> : <ArrowDown size={15} className="transition-transform duration-500 group-hover:translate-y-1" />}
            </button>
          </div>
        )}

        {showAll && items && (
          <div id="all-objects" className="mt-20" data-testid="member-all-grid">
            <Reveal>
              <div className="border-b border-ivory/10 pb-6">
                <h2 className="font-serif text-3xl font-medium text-ivory md:text-4xl">{m.allTitle}</h2>
              </div>
            </Reveal>
            <div className="mt-10 grid gap-x-10 gap-y-16 md:grid-cols-2 lg:grid-cols-3">
              {remaining.map((prop, i) => (
                <DarkCard key={prop.id} prop={prop} p={p} i={i} inquired={wasInquired(prop)} badge={m.inquiredBadge} newLabel={m.newBadge} lang={lang} />
              ))}
            </div>
          </div>
        )}

        <p className="mt-24 inline-block border border-bronze/40 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-bronze">
          {m.confidential}
        </p>
      </section>
    </MemberShell>
  );
}
